import { useState, useEffect, useCallback } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ErrorIcon from '@mui/icons-material/Error';
import SecurityIcon from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Services
import { authLogServices } from '../../../services/authLogServices';

// Styles
import { table_container_base, table_header_with_search } from '../../../static/styles';

const ACTION_CONFIG = {
  login_success: { icon: LoginIcon, color: 'success', label: 'Login Success' },
  login_failed: { icon: ErrorIcon, color: 'error', label: 'Login Failed' },
  logout: { icon: LogoutIcon, color: 'default', label: 'Logout' },
  token_refresh: { icon: RefreshIcon, color: 'info', label: 'Token Refresh' },
  password_recovery: { icon: SecurityIcon, color: 'warning', label: 'Password Recovery' },
  two_factor_success: { icon: CheckCircleIcon, color: 'success', label: '2FA Success' },
  two_factor_failed: { icon: BlockIcon, color: 'error', label: '2FA Failed' },
};

const StatsCard = ({ title, value, color = 'primary' }) => (
  <Card sx={{ minWidth: 120 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" color={`${color}.main`} sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export const AuthLogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [searchIp, setSearchIp] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      
      if (searchEmail) params.email = searchEmail;
      if (searchIp) params.ipAddress = searchIp;
      if (actionFilter) params.action = actionFilter;
      
      console.log('[AuthLogsTable] Loading logs with params:', params);
      const response = await authLogServices.getLogs(params);
      console.log('[AuthLogsTable] Response:', response.data);
      setLogs(response.data?.data || []);
      setTotal(response.data?.pagination?.total || 0);
    } catch (error) {
      console.error('[AuthLogsTable] Failed to load auth logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchEmail, searchIp, actionFilter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await authLogServices.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    loadLogs();
    loadStats();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm:ss A');
  };

  const getSessionDuration = (createdAt, logoutAt) => {
    if (!logoutAt) return 'Active';
    const duration = moment.duration(moment(logoutAt).diff(moment(createdAt)));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <Box>
      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatsCard title="Total Logins" value={stats.totalLogins} color="primary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatsCard title="Today Logins" value={stats.todayLogins} color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatsCard title="Failed Logins" value={stats.failedLogins} color="error" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatsCard title="Today Failed" value={stats.todayFailed} color="warning" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatsCard title="Blocked IPs" value={stats.blockedIps} color="error" />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ ...table_header_with_search, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by email..."
            value={searchEmail}
            onChange={(e) => { setSearchEmail(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            placeholder="Search by IP..."
            value={searchIp}
            onChange={(e) => { setSearchIp(e.target.value); setPage(0); }}
            sx={{ width: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="login_success">Login Success</MenuItem>
              <MenuItem value="login_failed">Login Failed</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
              <MenuItem value="two_factor_success">2FA Success</MenuItem>
              <MenuItem value="two_factor_failed">2FA Failed</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={table_container_base}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>IP Address</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Session</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No logs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const config = ACTION_CONFIG[log.action] || { color: 'default', label: log.action };
                const Icon = config.icon || LoginIcon;
                
                return (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(log.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<Icon sx={{ fontSize: 16 }} />}
                        label={config.label}
                        color={config.color}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{log.email || '-'}</TableCell>
                    <TableCell>{log.userName || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.action === 'login_success' && (
                        <Typography variant="body2" color={log.logoutAt ? 'text.secondary' : 'success.main'}>
                          {getSessionDuration(log.createdAt, log.logoutAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.failReason && (
                        <Typography variant="body2" color="error">
                          {log.failReason}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

